/* eslint-disable @typescript-eslint/naming-convention */
import { useCallback, useContext, useMemo } from "react";

// Context
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// types
import { PlanDetails, SwiggyOneGratificationCard } from "@swiggy-private/cart-types";

// constants
import { GRATIFICATION_USECASES, PLAN_ADDITION_SOURCE } from "@cart-client/constants/index";
import {
    DEFAULT_GRATIFICATION_DURATION,
    isGratificationPopupVariantAnimation,
    GRATIFICATION_POPUP_TEXTS,
    getPlanName,
    isOneLite,
} from "@swiggy-private/super-ui";
import { CLICK_ACTIONS, IMPRESSION_ACTIONS, SCREEN_NAME } from "@cart-client/analytics/constants";

// utils
import { format, isEmpty } from "@swiggy-private/js-utils";

export const useGratificationPopupAnalytics = (
    gratificationComms: SwiggyOneGratificationCard | null,
    source: PLAN_ADDITION_SOURCE,
    usecase: GRATIFICATION_USECASES,
    planDetails?: PlanDetails,
) => {
    const { clickEvent, impressionEvent } = useContext(AnalyticsContext);

    const contextValue = useMemo(() => {
        if (isGratificationPopupVariantAnimation(gratificationComms?.variant)) {
            return {
                savings_callout: format(
                    GRATIFICATION_POPUP_TEXTS.SAVINGS_CALLOUT,
                    gratificationComms?.attribute_values.plan_in_cart_savings,
                    getPlanName(planDetails?.type),
                ),
                initial_bill_amount: gratificationComms?.attribute_values?.cart_total_plan_not_added || "-",
                final_bill_amount: gratificationComms?.attribute_values?.cart_total_plan_added || "-",
                copy_head:
                    !isEmpty(planDetails) && isOneLite(planDetails)
                        ? GRATIFICATION_POPUP_TEXTS.SWIGGY_ONE_LITE_ADDED_TEXT
                        : GRATIFICATION_POPUP_TEXTS.SWIGGY_ONE_ADDED_TEXT,
                cta: GRATIFICATION_POPUP_TEXTS.CTA,
                visibility_time: GRATIFICATION_POPUP_TEXTS.VISIBILITY_TIME,
                source,
                usecase,
            };
        }
        return {
            savings_callout: gratificationComms?.header,
            copy_head: gratificationComms?.title,
            cta: gratificationComms?.cta.text,
            visibility_time: gratificationComms?.duration || DEFAULT_GRATIFICATION_DURATION,
            source,
            usecase,
            ...planDetails,
        };
    }, [
        gratificationComms?.variant,
        gratificationComms?.header,
        gratificationComms?.title,
        gratificationComms?.cta.text,
        gratificationComms?.duration,
        gratificationComms?.attribute_values.plan_in_cart_savings,
        gratificationComms?.attribute_values.cart_total_plan_not_added,
        gratificationComms?.attribute_values.cart_total_plan_added,
        source,
        usecase,
        planDetails,
    ]);

    const onImpressionGratificationPopup = useCallback(
        () =>
            impressionEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: IMPRESSION_ACTIONS.IMPRESSION_ONE_GRATIFICATION_POPUP,
                context: JSON.stringify(contextValue),
            }),
        [contextValue, impressionEvent],
    );

    const onClickGratificationPopupCta = useCallback(
        (colapsedBy: string) =>
            clickEvent({
                category: SCREEN_NAME.CHECKOUT,
                action: CLICK_ACTIONS.CLICK_ONE_GRATIFICATION_POPUP,
                context: JSON.stringify({ ...contextValue, colapsed_by: colapsedBy }),
            }),
        [clickEvent, contextValue],
    );

    return {
        onImpressionGratificationPopup,
        onClickGratificationPopupCta,
    };
};
